import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, CheckCircle, Sparkles, MessageCircleHeart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { freudQuote, specializations, testimonials } from '../mockData';
import { homeServices } from '../data/servicesContent';
import { heroBackgroundImage } from '../config/siteConfig';
import SocialLinks from '../components/SocialLinks';
import ArteVideoSection from '../components/ArteVideoSection';
import PartnersMarquee from '../components/PartnersMarquee';
import { allPartners } from '../data/partnersContent';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F3EB]">
      {/* Hero Section */}
      {/* "isolate" crée un contexte d'empilement : sans lui, les calques en
          z-index négatif (photo, voile) passent sous le fond crème de la page. */}
      <section className="relative isolate pt-32 pb-20 px-4 overflow-hidden">
        {/* Fond de la section d'en-tête : photo de Marseille (voir
            heroBackgroundImage dans src/config/siteConfig.js), avec un voile
            crème translucide pour garder le texte lisible par-dessus. */}
        {heroBackgroundImage ? (
          <>
            <img
              src={heroBackgroundImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover -z-20"
            />
            <div className="absolute inset-0 bg-[#F5F3EB]/70 -z-10"></div>
          </>
        ) : (
          <div className="absolute inset-0 bg-[#F5F3EB] -z-10"></div>
        )}

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#EAA93D]/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#21167D]/5 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className={`space-y-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="inline-block">
                <span className="px-4 py-2 bg-[#DCB071]/20 rounded-full text-sm font-medium text-[#21167D] shadow-sm font-sans">
                  Psychologue & Psychothérapeute
                </span>
              </div>

              <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#21167D] leading-tight">
                Tamila Hachemi
              </h1>

              <p className="font-accent italic text-xl md:text-2xl text-[#4A4458] leading-relaxed">
                Psychologue à <span className="text-[#21167D] not-italic font-semibold">Marseille 1er</span>
              </p>

              <p className="text-lg text-[#4A4458] leading-relaxed">
                Profitez de mon accompagnement bienveillant pour avancer dans votre chemin de vie !
                Thérapie intersectionnelle, thérapie de couple, groupe de parole… Et bien plus encore !
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/rendez-vous">
                  <Button size="lg" className="shadow-lg text-base">
                    Prendre rendez-vous
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/qui-suis-je">
                  <Button size="lg" variant="outline" className="text-base">
                    Découvrir mon parcours
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <span className="text-sm text-[#4A4458]">Suivez-moi :</span>
                <SocialLinks />
              </div>
            </div>

            {/* Hero Image */}
            <div className={`relative max-w-[320px] mx-auto lg:max-w-none lg:mx-0 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="absolute -bottom-5 -right-5 w-full h-full bg-[#DCB071] rounded-t-[4rem] rounded-b-2xl -z-10"></div>
              <div className="relative rounded-t-[4rem] rounded-b-2xl overflow-hidden shadow-2xl border-4 border-[#F5F3EB]">
                <img
                  src="/Tam_1.png"
                  alt="Tamila Hachemi, psychologue"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 px-4 bg-[#F5F3EB]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative">
            <Quote className="w-16 h-16 text-[#DCB071] mx-auto mb-6" />
            <blockquote className="font-accent italic text-2xl md:text-3xl text-[#4A4458] leading-relaxed mb-6">
              "{freudQuote.text}"
            </blockquote>
            <cite className="text-lg font-semibold text-[#21167D] not-italic font-sans">
              — {freudQuote.author}
            </cite>
          </div>
        </div>
      </section>

      {/* Specializations Section */}
      <section className="py-20 px-4 bg-[#DCB071]/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-semibold text-[#21167D] mb-4">
              Une approche intersectionnelle
            </h2>
            <p className="font-accent italic text-xl text-[#4A4458] max-w-3xl mx-auto">
              Spécialisée dans les questions d'identités intersectionnelles, croisant culture, genre, sexualité, neuroatypie et spiritualité
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specializations.map((spec, index) => (
              <div
                key={index}
                className="group p-6 bg-[#FDFBF5] rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-[#DCB071]/30"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#21167D] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                  <p className="text-lg font-medium text-[#21167D]">{spec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 bg-[#F5F3EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-[#DCB071]/20 rounded-full mb-4">
              <Sparkles className="w-5 h-5 text-[#21167D]" />
              <span className="text-sm font-semibold text-[#21167D] font-sans">Mes Services</span>
            </div>
            <h2 className="font-display text-4xl font-semibold text-[#21167D] mb-4">
              Un accompagnement adapté à vos besoins
            </h2>
            <p className="font-accent italic text-xl text-[#4A4458] max-w-3xl mx-auto">
              Suivi individuel, thérapie de couple, groupe de parole, interventions en organisme et prises de parole
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Offres de la plaquette (homeServices dans src/data/servicesContent.js) */}
            {homeServices.map((service) => (
              <Link key={service.id} to={`/services#${service.category}`} className="block">
                <Card className="group h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-[#FDFBF5]">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="w-16 h-16 bg-[#DCB071]/20 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                      <img src={service.icon} alt="" className="w-10 h-10 object-contain" />
                    </div>
                    <h3 className="font-accent font-semibold text-xl text-[#21167D] mb-3">{service.title}</h3>
                    <p className="text-[#4A4458] mb-4 leading-relaxed flex-1">{service.description}</p>
                    <div className="flex items-center justify-between gap-4 pt-4 border-t border-[#DCB071]/30">
                      <span className="text-sm text-[#4A4458]">{service.duration}</span>
                      <span className="text-base font-bold text-[#21167D] text-right whitespace-nowrap">{service.price}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline">
                Voir tous les services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Vidéo Arte */}
      <ArteVideoSection />

      {/* Tous les partenaires (src/data/partnersContent.js) */}
      <PartnersMarquee title="Ils m'ont fait confiance" partners={allPartners} />

      {/* Testimonials */}
      <section className="py-20 px-4 bg-[#DCB071]/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-semibold text-[#21167D] mb-4">
              Témoignages
            </h2>
            <p className="font-accent italic text-xl text-[#4A4458]">
              Ce que disent mes clients
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* TODO [À COMPLÉTER] : textes des témoignages (avec l'accord des
                patient·e·s concerné·e·s). Emplacements prêts à être remplis
                dans src/mockData.js (export testimonials). */}
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="bg-[#FDFBF5] shadow-lg hover:shadow-xl transition-shadow border-2 border-dashed border-[#DCB071]/50"
              >
                <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                  <MessageCircleHeart className="w-8 h-8 text-[#DCB071]" />
                  <p className="font-accent italic text-[#4A4458] leading-relaxed text-lg">
                    Témoignage à venir
                  </p>
                  <p className="text-sm text-[#4A4458]/70">[À COMPLÉTER]</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#21167D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl font-semibold text-[#F5F3EB] mb-6">
            Prêt à commencer votre cheminement ?
          </h2>
          <p className="font-accent italic text-xl text-[#F5F3EB]/85 mb-8 leading-relaxed">
            Prenez le premier pas vers un mieux-être. Contactez-moi pour une première consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/rendez-vous">
              <Button size="lg" className="bg-[#EAA93D] text-[#21167D] hover:bg-[#F5F3EB] hover:text-[#21167D] shadow-xl text-base">
                Prendre rendez-vous
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-[#F5F3EB] text-[#F5F3EB] hover:bg-[#F5F3EB] hover:text-[#21167D] text-base">
                Me contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
